import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { ResellerUser, UserRole, UserStatus, CreateUserData, UpdateUserData, ApiError } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/utils';
import {
  Search,
  Loader2,
  Users,
  Plus,
  ArrowLeft,
  Pencil,
  Key,
  UserCheck,
  UserX,
  RefreshCw,
  LogIn,
} from 'lucide-react';

type DialogMode = 'create' | 'edit' | 'password' | null;

export function AdminUsersPage() {
  const navigate = useNavigate();
  const { startImpersonation } = useAuth();
  const [users, setUsers] = useState<ResellerUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Dialog state
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedUser, setSelectedUser] = useState<ResellerUser | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: 'reseller' as UserRole,
    company_name: '',
    first_name: '',
    last_name: '',
    sf_reseller_account_id: '',
  });

  useEffect(() => {
    loadUsers();
  }, [statusFilter]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const filter = statusFilter === 'all' ? undefined : statusFilter;
      const { users } = await api.getUsers(filter, search || undefined);
      setUsers(users);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    loadUsers();
  };

  const openCreateDialog = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      role: 'reseller',
      company_name: '',
      first_name: '',
      last_name: '',
      sf_reseller_account_id: '',
    });
    setDialogError('');
    setDialogMode('create');
  };

  const openEditDialog = (user: ResellerUser) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      confirmPassword: '',
      role: user.role,
      company_name: user.company_name || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      sf_reseller_account_id: user.sf_reseller_account_id || '',
    });
    setDialogError('');
    setDialogMode('edit');
  };

  const openPasswordDialog = (user: ResellerUser) => {
    setSelectedUser(user);
    setFormData({
      ...formData,
      password: '',
      confirmPassword: '',
    });
    setDialogError('');
    setDialogMode('password');
  };

  const closeDialog = () => {
    setDialogMode(null);
    setSelectedUser(null);
    setDialogError('');
  };

  const handleCreateUser = async () => {
    if (formData.password !== formData.confirmPassword) {
      setDialogError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setDialogError('Password must be at least 8 characters');
      return;
    }

    if (formData.role === 'reseller' && !formData.sf_reseller_account_id) {
      setDialogError('Salesforce Reseller Account ID is required for reseller users');
      return;
    }

    setIsSaving(true);
    setDialogError('');

    try {
      const data: CreateUserData = {
        email: formData.email,
        password: formData.password,
        role: formData.role,
      };

      if (formData.role === 'reseller') {
        data.company_name = formData.company_name || undefined;
        data.first_name = formData.first_name || undefined;
        data.last_name = formData.last_name || undefined;
        data.sf_reseller_account_id = formData.sf_reseller_account_id;
      }

      await api.createUser(data);
      closeDialog();
      loadUsers();
    } catch (err) {
      const apiError = err as ApiError;
      setDialogError(apiError.error || 'Failed to create user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setIsSaving(true);
    setDialogError('');

    try {
      const data: UpdateUserData = {};

      if (formData.email !== selectedUser.email) {
        data.email = formData.email;
      }
      if (selectedUser.role === 'reseller') {
        if (formData.company_name !== (selectedUser.company_name || '')) {
          data.company_name = formData.company_name;
        }
        if (formData.first_name !== (selectedUser.first_name || '')) {
          data.first_name = formData.first_name;
        }
        if (formData.last_name !== (selectedUser.last_name || '')) {
          data.last_name = formData.last_name;
        }
        if (formData.sf_reseller_account_id !== (selectedUser.sf_reseller_account_id || '')) {
          data.sf_reseller_account_id = formData.sf_reseller_account_id;
        }
      }

      await api.updateUser(selectedUser.id, data);
      closeDialog();
      loadUsers();
    } catch (err) {
      const apiError = err as ApiError;
      setDialogError(apiError.error || 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    if (formData.password !== formData.confirmPassword) {
      setDialogError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setDialogError('Password must be at least 8 characters');
      return;
    }

    setIsSaving(true);
    setDialogError('');

    try {
      await api.updateUserPassword(selectedUser.id, formData.password);
      closeDialog();
    } catch (err) {
      const apiError = err as ApiError;
      setDialogError(apiError.error || 'Failed to reset password');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (user: ResellerUser) => {
    const newStatus: UserStatus = user.status === 'active' ? 'disabled' : 'active';
    try {
      await api.updateUserStatus(user.id, newStatus);
      loadUsers();
    } catch (err) {
      const apiError = err as ApiError;
      console.error('Failed to update user status:', apiError.error);
    }
  };

  const handleImpersonate = async (user: ResellerUser) => {
    if (!user.reseller_id) {
      console.error('User has no associated reseller');
      return;
    }

    setIsImpersonating(true);
    try {
      const resellerName = user.company_name ||
        `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
        user.email;
      await startImpersonation(user.reseller_id, resellerName);
      // Navigate to reseller dashboard after impersonation starts
      navigate('/');
    } catch (err) {
      const apiError = err as ApiError;
      console.error('Failed to start impersonation:', apiError.error);
      setIsImpersonating(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
              <p className="text-gray-600 mt-1">Manage admin and reseller users</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
            <Button variant="outline" onClick={loadUsers}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>
                  {users.length} user{users.length !== 1 ? 's' : ''} found
                </CardDescription>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-9 w-[200px]"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No users found</p>
              </div>
            ) : (
              <>
                {/* Mobile/tablet card view */}
                <div className="lg:hidden space-y-4">
                  {users.map((user) => (
                    <div key={user.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 break-all">{user.email}</p>
                          {(user.first_name || user.last_name) && (
                            <p className="text-sm text-gray-500">
                              {user.first_name} {user.last_name}
                            </p>
                          )}
                          {user.role === 'reseller' && (
                            <p className="text-xs text-gray-400 font-mono break-all mt-1">
                              UUID: {user.id}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1 ml-2 flex-shrink-0">
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {user.role === 'admin' ? 'Admin' : 'Reseller'}
                          </Badge>
                          <Badge
                            variant={user.status === 'active' ? 'outline' : 'destructive'}
                            className={user.status === 'active' ? 'border-green-500 text-green-600' : ''}
                          >
                            {user.status === 'active' ? 'Active' : 'Disabled'}
                          </Badge>
                        </div>
                      </div>
                      {user.company_name && (
                        <div className="text-sm mb-3">
                          <p className="text-gray-500">Company</p>
                          <p className="text-gray-900">{user.company_name}</p>
                          {user.sf_reseller_account_id && (
                            <p className="text-xs text-gray-500 font-mono break-all">
                              {user.sf_reseller_account_id}
                            </p>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                          Created {formatDate(user.created_at)}
                        </p>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Edit"
                            onClick={() => openEditDialog(user)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Reset Password"
                            onClick={() => openPasswordDialog(user)}
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          {user.role !== 'admin' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={user.status === 'active' ? 'Disable User' : 'Enable User'}
                              onClick={() => handleToggleStatus(user)}
                            >
                              {user.status === 'active' ? (
                                <UserX className="h-4 w-4 text-red-500" />
                              ) : (
                                <UserCheck className="h-4 w-4 text-green-500" />
                              )}
                            </Button>
                          )}
                          {user.role !== 'admin' && user.reseller_id && user.status === 'active' && (
                            <>
                              <div className="w-px h-6 bg-gray-200 mx-1" />
                              <Button
                                variant="outline"
                                size="sm"
                                title="Login as this reseller"
                                onClick={() => handleImpersonate(user)}
                                disabled={isImpersonating}
                                className="text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700"
                              >
                                <LogIn className="h-4 w-4 mr-1" />
                                Login as
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table view */}
                <div className="hidden lg:block">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left text-sm text-gray-500">
                        <th className="pb-3 font-medium">Email</th>
                        <th className="pb-3 font-medium">Name</th>
                        <th className="pb-3 font-medium">Company</th>
                        <th className="pb-3 font-medium">Role</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Created</th>
                        <th className="pb-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="py-4">
                            <p className="font-medium text-gray-900">{user.email}</p>
                            {user.role === 'reseller' && (
                              <p className="text-xs text-gray-400 font-mono">
                                UUID: {user.id}
                              </p>
                            )}
                          </td>
                          <td className="py-4">
                            {user.first_name || user.last_name ? (
                              <p className="text-gray-900">
                                {user.first_name} {user.last_name}
                              </p>
                            ) : (
                              <p className="text-gray-400">-</p>
                            )}
                          </td>
                          <td className="py-4">
                            {user.company_name ? (
                              <div>
                                <p className="text-gray-900">{user.company_name}</p>
                                {user.sf_reseller_account_id && (
                                  <p className="text-xs text-gray-500 font-mono">
                                    {user.sf_reseller_account_id}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-gray-400">-</p>
                            )}
                          </td>
                          <td className="py-4">
                            <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                              {user.role === 'admin' ? 'Admin' : 'Reseller'}
                            </Badge>
                          </td>
                          <td className="py-4">
                            <Badge
                              variant={user.status === 'active' ? 'outline' : 'destructive'}
                              className={user.status === 'active' ? 'border-green-500 text-green-600' : ''}
                            >
                              {user.status === 'active' ? 'Active' : 'Disabled'}
                            </Badge>
                          </td>
                          <td className="py-4 text-sm text-gray-500">
                            {formatDate(user.created_at)}
                          </td>
                          <td className="py-4">
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Edit"
                                onClick={() => openEditDialog(user)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Reset Password"
                                onClick={() => openPasswordDialog(user)}
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              {user.role !== 'admin' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title={user.status === 'active' ? 'Disable User' : 'Enable User'}
                                  onClick={() => handleToggleStatus(user)}
                                >
                                  {user.status === 'active' ? (
                                    <UserX className="h-4 w-4 text-red-500" />
                                  ) : (
                                    <UserCheck className="h-4 w-4 text-green-500" />
                                  )}
                                </Button>
                              )}
                              {user.role !== 'admin' && user.reseller_id && user.status === 'active' && (
                                <>
                                  <div className="w-px h-6 bg-gray-200 mx-1" />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    title="Login as this reseller"
                                    onClick={() => handleImpersonate(user)}
                                    disabled={isImpersonating}
                                    className="text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700"
                                  >
                                    <LogIn className="h-4 w-4 mr-1" />
                                    Login as
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogMode === 'create' || dialogMode === 'edit'} onOpenChange={() => closeDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' ? 'Create New User' : 'Edit User'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'create'
                ? 'Add a new admin or reseller user'
                : 'Update user information'}
            </DialogDescription>
          </DialogHeader>

          {dialogError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{dialogError}</div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            {dialogMode === 'create' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value: UserRole) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reseller">Reseller</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                  />
                </div>
              </>
            )}

            {(formData.role === 'reseller' || (dialogMode === 'edit' && selectedUser?.role === 'reseller')) && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sf_reseller_account_id">
                    Salesforce Reseller Account ID {dialogMode === 'create' && '*'}
                  </Label>
                  <Input
                    id="sf_reseller_account_id"
                    value={formData.sf_reseller_account_id}
                    onChange={(e) =>
                      setFormData({ ...formData, sf_reseller_account_id: e.target.value })
                    }
                    required={dialogMode === 'create' && formData.role === 'reseller'}
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={dialogMode === 'create' ? handleCreateUser : handleUpdateUser}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : dialogMode === 'create' ? (
                'Create User'
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={dialogMode === 'password'} onOpenChange={() => closeDialog()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>

          {dialogError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{dialogError}</div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-new-password">Confirm Password</Label>
              <Input
                id="confirm-new-password"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
